import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import LandingNav from '../components/LandingNav';
import LandingFooter from '../components/LandingFooter';
import BetaForm from '../BetaForm';
import { Check } from "lucide-react";

const PricingPage = () => {
  const [isYearly, setIsYearly] = useState(true);
  const [showWaitlistDialog, setShowWaitlistDialog] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) setIsLoggedIn(true);
  }, []);

  const handleStartPlan = () => {
    if (!isLoggedIn) {
      setShowWaitlistDialog(true);
      return;
    }
    navigate(`/checkout?plan=${isYearly ? 'yearly' : 'monthly'}`);
  };

  const homeCookFeatures = [
    "Your personal recipe library",
    "Quick recipe import",
    "Shareable recipe links",
    "Custom filters & categories",
    "Public bio with social links",
  ];

  const creatorFeatures = [
    "Everything in Home Cook",
    "Analytics dashboard",
    "Custom branding",
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#EEE7D8' }}>
      <LandingNav
        onLogin={() => navigate('/?login=true')}
        onSignUp={() => navigate('/?signup=true')}
        onJoinWaitlist={() => setShowWaitlistDialog(true)}
        isLoggedIn={isLoggedIn}
      />

      <div className="pt-[115px] pb-20 px-4">
        <div className="mx-auto text-center mb-12 pt-8" style={{ maxWidth: '580px' }}>
          <h1 
            className="text-4xl md:text-5xl lg:text-6xl mb-4"
            style={{ 
              fontFamily: "'Instrument Serif', serif",
              color: '#51120A',
              lineHeight: '1.1'
            }}
          >
            Less than the smoothie you now make at&nbsp;home
          </h1>
          <p 
            className="text-lg md:text-xl max-w-2xl mx-auto"
            style={{ 
              fontFamily: "'Bricolage Grotesque', sans-serif",
              color: '#51120A',
              opacity: 0.8
            }}
          >
            Wellby keeps every recipe you love in one simple place.
          </p>
        </div>

        <div className="flex items-center justify-center gap-4 mb-12">
          <span 
            className="text-sm font-medium cursor-pointer"
            style={{ 
              fontFamily: "'Bricolage Grotesque', sans-serif",
              color: !isYearly ? '#51120A' : '#51120A80',
            }}
            onClick={() => setIsYearly(false)}
          >
            Monthly
          </span>
          <button
            onClick={() => setIsYearly(!isYearly)}
            className="relative inline-flex h-7 w-12 items-center rounded-full transition-colors"
            style={{ backgroundColor: isYearly ? '#51120A' : '#D6D0C2' }}
          >
            <span
              className="inline-block h-5 w-5 transform rounded-full bg-white transition-transform"
              style={{ transform: isYearly ? 'translateX(24px)' : 'translateX(4px)' }}
            />
          </button>
          <span 
            className="text-sm font-medium cursor-pointer"
            style={{ 
              fontFamily: "'Bricolage Grotesque', sans-serif",
              color: isYearly ? '#51120A' : '#51120A80',
            }}
            onClick={() => setIsYearly(true)}
          >
            Yearly
          </span>
        </div>

        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-6 items-start">
          <div 
            className="rounded-2xl p-8 relative flex flex-col"
            style={{ 
              backgroundColor: '#F3EEE4',
              border: '2px solid #51120A',
            }}
          >
            <h2 
              className="text-2xl font-bold mb-1"
              style={{ 
                fontFamily: "'Bricolage Grotesque', sans-serif",
                color: '#51120A'
              }}
            >
              Home Cook
            </h2>
            <div className="mb-6">
              <div className="flex items-baseline gap-1">
                <span 
                  className="text-5xl font-bold"
                  style={{ 
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                    color: '#51120A'
                  }}
                >
                  ${isYearly ? '8.33' : '10'}
                </span>
                <span 
                  className="text-lg"
                  style={{ 
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                    color: '#51120A'
                  }}
                >
                  / month
                </span>
              </div>
              <p 
                className="text-sm mt-1"
                style={{ 
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  color: '#51120A80',
                  visibility: isYearly ? 'visible' : 'hidden'
                }}
              >
                billed yearly ($100)
              </p>
            </div>
            
            <ul className="space-y-3 mb-8 flex-grow">
              {homeCookFeatures.map((feature, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check size={18} className="mt-0.5 flex-shrink-0" style={{ color: '#51120A' }} />
                  <span 
                    className="text-sm"
                    style={{ 
                      fontFamily: "'Bricolage Grotesque', sans-serif",
                      color: '#51120A'
                    }}
                  >
                    {feature}
                  </span>
                </li>
              ))}
            </ul>

            <Button 
              onClick={handleStartPlan}
              className="w-full py-6 text-base font-medium text-white transition-colors mt-auto"
              style={{ 
                backgroundColor: '#51120A',
                fontFamily: "'Bricolage Grotesque', sans-serif",
              }}
            >
              {isLoggedIn ? 'Start Home Cook Plan' : 'Join Waitlist'}
            </Button>
          </div>

          <div 
            className="rounded-2xl p-8 relative flex flex-col"
            style={{ 
              backgroundColor: '#F3EEE4',
              border: '1px solid #D6D0C2',
            }}
          >
            <div className="flex items-center gap-3 mb-1">
              <h2 
                className="text-2xl font-bold"
                style={{ 
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  color: '#51120A'
                }}
              >
                Creator
              </h2>
              <span 
                className="text-xs px-3 py-1 rounded-full"
                style={{ 
                  backgroundColor: '#D6D0C2',
                  color: '#51120A',
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                }}
              >
                Coming Soon
              </span>
            </div>
            <div className="mb-6">
              <div className="flex items-baseline gap-1">
                <span 
                  className="text-5xl font-bold"
                  style={{ 
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                    color: '#51120A'
                  }}
                >
                  ${isYearly ? '16.67' : '20'}
                </span>
                <span 
                  className="text-lg"
                  style={{ 
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                    color: '#51120A'
                  }}
                >
                  / month
                </span>
              </div>
              <p 
                className="text-sm mt-1"
                style={{ 
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  color: '#51120A80',
                  visibility: isYearly ? 'visible' : 'hidden'
                }}
              >
                billed yearly ($200)
              </p>
            </div>
            
            <ul className="space-y-3 mb-8 flex-grow">
              {creatorFeatures.map((feature, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check size={18} className="mt-0.5 flex-shrink-0" style={{ color: '#51120A' }} />
                  <span 
                    className="text-sm"
                    style={{ 
                      fontFamily: "'Bricolage Grotesque', sans-serif",
                      color: '#51120A'
                    }}
                  >
                    {feature}
                  </span>
                </li>
              ))}
            </ul>

            <Button 
              disabled
              className="w-full py-6 text-base font-medium transition-colors cursor-not-allowed mt-auto disabled:opacity-100"
              style={{ 
                backgroundColor: '#D6D0C2',
                color: '#51120A',
                fontFamily: "'Bricolage Grotesque', sans-serif",
                opacity: 1,
              }}
            >
              Coming Soon
            </Button>
          </div>
        </div>
      </div>

      <BetaForm open={showWaitlistDialog} onOpenChange={setShowWaitlistDialog} />

      <LandingFooter />
    </div>
  );
};

export default PricingPage;
